import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';

export interface UnsavedChangesGuard {
  /** Dialogue « Modifications non enregistrées », à rendre dans la page. */
  dialog: JSX.Element;
  /**
   * Laisse passer la prochaine navigation sans garde : à appeler juste avant
   * une navigation qui suit un enregistrement ou une action réussie.
   */
  allowNextNavigation: () => void;
}

/**
 * Garde de sortie d'un formulaire modifié : toute navigation (lien retour,
 * changement d'onglet ou de paramètre d'URL, retour navigateur) passe par
 * « Modifications non enregistrées · Rester / Quitter », et la fermeture ou le
 * rechargement de l'onglet par l'alerte native du navigateur.
 *
 * Le formulaire remonte son état modifié (`onDirtyChange`) ; la page appelle
 * ce hook une fois et rend `dialog`.
 */
export function useUnsavedChangesGuard(isDirty: boolean): UnsavedChangesGuard {
  const dirtyRef = useRef(isDirty);
  const bypassRef = useRef(false);

  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return false;
      }
      return dirtyRef.current;
    },
    enableBeforeUnload: () => dirtyRef.current,
    withResolver: true,
  });

  const dialog = (
    <ConfirmDialog
      open={blocker.status === 'blocked'}
      onOpenChange={(open) => {
        if (!open) {
          blocker.reset?.();
        }
      }}
      title="Modifications non enregistrées"
      description="Vos modifications seront perdues si vous quittez ce formulaire sans l’enregistrer."
      cancelLabel="Rester"
      confirmLabel="Quitter"
      onConfirm={() => {
        blocker.proceed?.();
      }}
    />
  );

  return {
    dialog,
    allowNextNavigation: () => {
      bypassRef.current = true;
    },
  };
}

/** Remonte l'état modifié d'un formulaire à sa page ; repasse à `false` au démontage. */
export function useReportDirty(isDirty: boolean, onDirtyChange: ((dirty: boolean) => void) | undefined): void {
  const callbackRef = useRef(onDirtyChange);
  callbackRef.current = onDirtyChange;

  useEffect(() => {
    callbackRef.current?.(isDirty);
  }, [isDirty]);

  useEffect(
    () => () => {
      callbackRef.current?.(false);
    },
    [],
  );
}
